import { createContext, useRef, useContext, useMemo } from 'react';
import { WidgetId } from 'types/idTypes';
import { MultiEditOption, MultiEditWidgetConfig, MultiEditChangeOption } from '../Widget.types';
import { MultiEditConfigRef } from './MultiEditConfigProvider.types';

const defaultConfig = { config: {} };

const MultiEditConfigContext = createContext<MultiEditConfigRef>(defaultConfig as MultiEditConfigRef);

/**
 * A provider for the multi edit config
 *
 * For widgets: add their multi edit config to the context
 * For other components: access the multi edit config
 */
export const MultiEditConfigProvider = ({ children }: { children: React.ReactNode }) => {
  // a map with all the widget ids and their (optional) multi edit configs
  const multiEditConfigRef = useRef<MultiEditConfigRef>(defaultConfig as MultiEditConfigRef);

  /**
   * Adds the options for a multi select widget to the multiEditConfigRef
   *
   * @param widgetId
   * @param options
   */
  const addMultiSelectOptions = (widgetId: WidgetId, options: MultiEditWidgetConfig<any>) => {
    multiEditConfigRef.current.config[widgetId] = options;
  };

  /**
   * Gets the custom widget data update function for a multi select widget
   *
   * @param widgetId
   * @param updateFnName
   * @param option
   */
  const getCustomUpdateFunction = (
    widgetId: WidgetId,
    updateFnName: MultiEditChangeOption,
    option: MultiEditOption,
  ) => {
    const updateFunction = multiEditConfigRef.current?.config?.[widgetId]?.[option]?.[updateFnName];

    return updateFunction;
  };

  /**
   * Gets the custom widget data key for a multi select widget
   * @param widgetId
   * @param option
   */
  const getCustomKey = (widgetId: WidgetId, option: MultiEditOption) => {
    const customKey = multiEditConfigRef.current?.config?.[widgetId]?.[option]?.key;

    return customKey;
  };

  /**
   * Gets the custom  display function for a multi select widget
   * @param widgetId
   */
  const getCustomDisplayFunction = (widgetId: WidgetId) => {
    const displayFunction = multiEditConfigRef.current?.config?.[widgetId]?.displayMultiSelect;
    return displayFunction;
  };

  /**
   * Gets the custom value function for a multi select widget
   * @param widgetId
   * @param option
   */
  const getCustomValueFunction = (widgetId: WidgetId, option: MultiEditOption) => {
    const getValueFunction = multiEditConfigRef.current?.config?.[widgetId]?.[option]?.getValue;
    return getValueFunction;
  };

  const values = useMemo(
    () => ({
      config: multiEditConfigRef.current.config,
      addMultiSelectOptions,
      getCustomKey,
      getCustomUpdateFunction,
      getCustomDisplayFunction,
      getCustomValueFunction,
    }),
    [],
  );

  return <MultiEditConfigContext.Provider value={values}>{children}</MultiEditConfigContext.Provider>;
};

export const useMultiEditConfig = () => {
  const multiEditConfigRef = useContext(MultiEditConfigContext);

  return multiEditConfigRef;
};
